import store from 'state/store.js';
import { round } from 'utils/round-functions/round';
import { calculateFee, findCurrentFee } from 'utils/fee-functions/calculate-comission';
import { AvailableCurrency } from '@pano-project/utils/getLimitsForCurrency';
import { getWalletCurrencyLimits } from 'utils/hooks/digital-wallets/helpers';

export const getBaseAmountFromTotalCharge = ({
  chargeCurrency,
  chargeAmount = 0,
  predefinedFees,
  predefinedCurrencyInfo,
}: {
  chargeCurrency: string;
  chargeAmount: number;
  predefinedFees?: Record<string, any>;
  predefinedCurrencyInfo?: Record<string, any>;
}) => {
  const {
    profileInfo: { available_currencies: { currencies = [] } = {} },
    currentGateway,
    currentGateway: { payment_information: { currency_list = {} } = {} } = {},
    profileInfo: { base_currency = '', transaction_type = '' } = {},
  } = store.getState();

  const fees = predefinedFees || currentGateway?.fees || {};

  const currencyInfo =
    predefinedCurrencyInfo ||
    getWalletCurrencyLimits(chargeCurrency) ||
    currency_list?.[chargeCurrency] ||
    currencies?.find((currency: AvailableCurrency) => currency.code === chargeCurrency);
  if (!currencyInfo) return null;

  const { rate } = currencyInfo;
  if (!chargeAmount || !rate) return null;

  const chargeAmountNumber = Number(chargeAmount);

  const chargeAmountFee = findCurrentFee(
    fees?.[chargeCurrency] || [],
    chargeAmountNumber,
    chargeCurrency,
  );
  if (!chargeAmountFee) return round(chargeAmountNumber / rate, base_currency);

  const { min_fee_amount, max_fee_amount, flat_fee_amount, percentage_fee } = chargeAmountFee;
  const maxFee = max_fee_amount ?? Infinity;
  const minFee = min_fee_amount ?? 0;
  const flatFee = flat_fee_amount ?? 0;
  const variable = (percentage_fee ?? 0) / 100;

  let amountWithFeeAdded;
  let amountWithFeeIncluded;

  if (transaction_type === 'pay-out') {
    amountWithFeeAdded =
      (chargeAmountNumber +
        Math.min(maxFee, Math.max(minFee, flatFee + variable * chargeAmountNumber))) /
      rate;
    amountWithFeeIncluded =
      (chargeAmountNumber +
        Math.min(
          maxFee,
          Math.max(minFee, flatFee + variable * ((chargeAmountNumber + flatFee) / (1 - variable))),
        )) /
      rate;
  }

  if (transaction_type === 'pay-in') {
    amountWithFeeAdded =
      (chargeAmountNumber -
        Math.min(
          maxFee,
          Math.max(minFee, flatFee + variable * ((chargeAmountNumber - flatFee) / (1 + variable))),
        )) /
      rate;
    amountWithFeeIncluded =
      (chargeAmountNumber -
        Math.min(maxFee, Math.max(minFee, flatFee + variable * chargeAmountNumber))) /
      rate;
  }

  const baseAmount = chargeAmountFee.is_included ? amountWithFeeIncluded : amountWithFeeAdded;

  // A fee larger than the charge makes the subtraction go below zero. A deposit can never
  // be negative, so floor it instead of passing the negative number on to the UI and payData.
  return round(Math.max(0, baseAmount), base_currency);
};

/**
 * Convert one retry "try a lower amount" option into the amounts the cashier stores.
 *
 * The backend sends these options in the processing currency, as the amount before the fee.
 * So the base amount comes from the exchange rate alone, and the total to charge is derived
 * back from that base amount with the fee added — never by subtracting a fee from the option.
 */
export const getRetryAmountsFromOffer = ({
  offerAmount,
  offerCurrency,
  displayCurrency,
  gateway,
}: {
  offerAmount: number | string;
  offerCurrency?: string;
  displayCurrency?: string;
  gateway?: Record<string, any>;
}) => {
  const {
    profileInfo: { available_currencies: { currencies = [] } = {} },
    currentGateway,
    profileInfo: { base_currency = '', transaction_type = '' } = {},
  } = store.getState();

  const gatewayToUse = gateway || currentGateway;
  const offerAmountNumber = Number(offerAmount);

  if (!offerAmountNumber || isNaN(offerAmountNumber)) return null;

  // No conversion: the option already is the base amount, the fee goes on top.
  if (!offerCurrency || offerCurrency === base_currency) {
    const { total } = calculateFee(
      gatewayToUse?.fees || {},
      offerAmountNumber,
      base_currency,
      transaction_type,
    );
    const totalChargeNumber = Number(total);
    const amount = round(offerAmountNumber, base_currency);
    const totalCharge = round(
      isNaN(totalChargeNumber) ? offerAmountNumber : totalChargeNumber,
      base_currency,
    );
    return { amount, totalCharge, display: amount };
  }

  const currency_list = gatewayToUse?.payment_information?.currency_list || {};
  const currencyInfo =
    getWalletCurrencyLimits(offerCurrency) ||
    currency_list?.[offerCurrency] ||
    currencies?.find((currency: AvailableCurrency) => currency.code === offerCurrency);

  const rate = Number(currencyInfo?.rate);
  if (!rate) return null;

  const baseAmount = round(offerAmountNumber / rate, base_currency);
  const totalCharge = getTotalChargeFromBaseCurrencyAmount({
    chargeCurrency: offerCurrency,
    amount: baseAmount,
    gateway: gatewayToUse,
  });

  const chargeInOfferCurrency = round(
    totalCharge === null ? offerAmountNumber : Number(totalCharge),
    offerCurrency,
  );

  // The pill must read in whichever currency the customer is editing, and must agree with
  // the amount field beside it. Editing in the base currency that is the base amount;
  // editing in any processing currency it is the charge for that currency, fee included.
  // Showing the raw option here made the pill disagree with the field by the whole fee.
  const display = (() => {
    if (!displayCurrency || displayCurrency === base_currency) return baseAmount;
    if (displayCurrency === offerCurrency) return chargeInOfferCurrency;
    const chargeInDisplayCurrency = getTotalChargeFromBaseCurrencyAmount({
      chargeCurrency: displayCurrency,
      amount: baseAmount,
      gateway: gatewayToUse,
    });
    return chargeInDisplayCurrency === null
      ? baseAmount
      : round(Number(chargeInDisplayCurrency), displayCurrency);
  })();

  return {
    amount: baseAmount,
    totalCharge: chargeInOfferCurrency,
    display,
  };
};

export const getTotalChargeFromBaseCurrencyAmount = ({
  chargeCurrency,
  gateway,
  amount = 0,
  predefinedFees,
  predefinedCurrencyInfo,
}: {
  chargeCurrency: string;
  gateway: Record<string, any> | undefined;
  amount?: number;
  predefinedFees?: Record<string, any>;
  predefinedCurrencyInfo?: Record<string, any>;
}) => {
  const {
    profileInfo: { available_currencies: { currencies = [] } = {} },
    profileInfo: { transaction_type = '' } = {},
  } = store.getState();

  const fees = predefinedFees || gateway?.fees || {};
  const currency_list = gateway?.payment_information?.currency_list || {};

  const currencyInfo =
    predefinedCurrencyInfo ||
    getWalletCurrencyLimits(chargeCurrency) ||
    currency_list?.[chargeCurrency] ||
    currencies?.find((currency: AvailableCurrency) => currency.code === chargeCurrency);

  if (!currencyInfo) return null;

  const { rate } = currencyInfo;
  if (!amount || !rate) return null;

  const total = Number(amount * rate);

  const amountFee = findCurrentFee(fees?.[chargeCurrency] || [], total, chargeCurrency);
  if (!amountFee) return round(total, chargeCurrency);

  const { min_fee_amount, max_fee_amount, flat_fee_amount, percentage_fee } = amountFee;
  const maxFee = max_fee_amount ?? Infinity;
  const minFee = min_fee_amount ?? 0;
  const flatFee = flat_fee_amount ?? 0;
  const variable = (percentage_fee ?? 0) / 100;

  let chargeAmountWithFeeAdded;
  let chargeAmountWithFeeIncluded;

  if (transaction_type === 'pay-out') {
    chargeAmountWithFeeAdded = Math.min(
      total - minFee,
      Math.max(total - maxFee, (total - flatFee) / (1 + variable)),
    );
    chargeAmountWithFeeIncluded =
      total - Math.min(maxFee, Math.max(minFee, flatFee + variable * total));
  }

  if (transaction_type === 'pay-in') {
    chargeAmountWithFeeAdded =
      total + Math.min(maxFee, Math.max(minFee, flatFee + variable * total));
    chargeAmountWithFeeIncluded = Math.max(
      total + minFee,
      Math.min(total + maxFee, (total + flatFee) / (1 - variable)),
    );
  }

  return amountFee.is_included
    ? round(chargeAmountWithFeeIncluded, chargeCurrency)
    : round(chargeAmountWithFeeAdded, chargeCurrency);
};
